/* eslint-disable camelcase */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable max-lines */
/* eslint-disable max-lines-per-function */
import { CSSProperties, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Checkbox, EdsProvider, Icon } from '@equinor/eds-core-react';
import { CenteredLoader } from '../../components/CenteredLoader/CenteredLoader';
import { view_column } from '@equinor/eds-icons';
import { EdsDataGrid } from '@equinor/eds-data-grid-react';
import type { VisibilityState } from '@tanstack/react-table';
import { useFetchScenarios } from '../../hooks/useFetchScenarios';
import { ProgressCell } from './ProgressCell';
import * as Styled from './SimulationTable.styled';
import { useFetchAnalogueModels } from '../../hooks/useFetchAnalogueModels';
import { AnalogueModelConfigurationDto } from '../../api/generated';

// Helper function to parse JSON scenario data
const parseScenarioData = (
  jsonData: string | null | undefined,
  iniFile?: AnalogueModelConfigurationDto,
) => {
  const fallbackData = {
    template: iniFile?.template?.value ?? '-',
    basinSlope: iniFile?.basinSlope?.value ?? '-',
    composition: iniFile?.composition?.value ?? '-',
    riverLength: iniFile?.riverLength?.value ?? '-',
    simStopTime: iniFile?.simStopTime?.value ?? '-',
    channelWidth: iniFile?.channelWidth?.value ?? '-',
    subsidenceSea: iniFile?.subsidenceSea?.value ?? '-',
    waveDirection: iniFile?.waveDirection?.value ?? '-',
    waveHeightFin: iniFile?.waveHeightFin?.value ?? '-',
    waveHeightIni: iniFile?.waveHeightIni?.value ?? '-',
    outputInterval: iniFile?.outputInterval?.value ?? '-',
    subsidenceLand: iniFile?.subsidenceLand?.value ?? '-',
    tidalAmplitude: iniFile?.tidalAmplitude?.value ?? '-',
    riverDischargeFin: iniFile?.riverDischargeFin?.value ?? '-',
    riverDischargeIni: iniFile?.riverDischargeIni?.value ?? '-',
  };

  if (!jsonData || jsonData.trim().length === 0) {
    return fallbackData;
  }

  try {
    const data = JSON.parse(jsonData);
    return {
      template: data.template?.value ?? fallbackData.template,
      basinSlope: data.basinslope?.value ?? fallbackData.basinSlope,
      composition: data.composition?.value ?? fallbackData.composition,
      riverLength: data.riverlength?.value ?? fallbackData.riverLength,
      simStopTime: data.simstoptime?.value ?? fallbackData.simStopTime,
      channelWidth: data.channelwidth?.value ?? fallbackData.channelWidth,
      subsidenceSea: data.subsidencesea?.value ?? fallbackData.subsidenceSea,
      waveDirection: data.wavedirection?.value ?? fallbackData.waveDirection,
      waveHeightFin: data.waveheightfin?.value ?? fallbackData.waveHeightFin,
      waveHeightIni: data.waveheightini?.value ?? fallbackData.waveHeightIni,
      outputInterval: data.outputinterval?.value ?? fallbackData.outputInterval,
      subsidenceLand: data.subsidenceland?.value ?? fallbackData.subsidenceLand,
      tidalAmplitude: data.tidalamplitude?.value ?? fallbackData.tidalAmplitude,
      riverDischargeFin:
        data.riverdischargefin?.value ?? fallbackData.riverDischargeFin,
      riverDischargeIni:
        data.riverdischargeini?.value ?? fallbackData.riverDischargeIni,
    };
  } catch {
    return fallbackData;
  }
};

export const SimulationTable = () => {
  const navigate = useNavigate();
  const { data: scenarios = [], isLoading: isScenariosLoading } =
    useFetchScenarios();
  const { data: models = [], isLoading: isModelsLoading } =
    useFetchAnalogueModels();

  const modelsIniFileMap = models
    .map((model) => ({
      modelId: model.analogueModelId,
      iniFile: model.iniParameters,
    }))
    .reduce((acc, { modelId, iniFile }) => {
      acc[modelId] = iniFile;
      return acc;
    }, {} as Record<string, AnalogueModelConfigurationDto>);

  const scenariosWithoutModel = scenarios.filter(
    (scenario) => !scenario.analogue_model_id,
  );

  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({
    template: true,
    basinSlope: true,
    composition: true,
    riverLength: false,
    simStopTime: false,
    channelWidth: false,
    subsidenceSea: false,
    waveDirection: false,
    waveHeightFin: false,
    waveHeightIni: false,
    outputInterval: false,
    subsidenceLand: false,
    tidalAmplitude: false,
    riverDischargeFin: false,
    riverDischargeIni: false,
    orchestration_status: true,
    created_at: true,
  });
  const [isSideSheetOpen, setIsSideSheetOpen] = useState(false);
  const anchorRef = useRef<HTMLButtonElement>(null);

  const handleNavigateToSimulation = (scenarioId: string | undefined) => {
    if (scenarioId) {
      navigate(`/simulations/${scenarioId}`);
    }
  };

  const toggleColumn = (columnId: string) => {
    setColumnVisibility((prev) => ({
      ...prev,
      [columnId]: !prev[columnId],
    }));
  };

  const headerStyle = (): CSSProperties => ({ verticalAlign: 'middle' });

  if (isScenariosLoading || isModelsLoading) return <CenteredLoader />;

  return (
    <>
      <Styled.Table>
        <Styled.ColumnsButton>
          <Button
            ref={anchorRef}
            variant="outlined"
            onClick={() => setIsSideSheetOpen(!isSideSheetOpen)}
            aria-label="Toggle columns"
          >
            Toggle columns&nbsp;
            <Icon data={view_column} />
          </Button>
        </Styled.ColumnsButton>
        <EdsDataGrid
          enableSorting
          enableColumnFiltering
          emptyMessage="No scenarios found"
          columnResizeMode="onChange"
          rows={scenariosWithoutModel}
          scrollbarHorizontal
          stickyHeader
          headerStyle={headerStyle}
          width="100%"
          columnVisibility={columnVisibility}
          columns={[
            {
              header: 'Template',
              id: 'template',
              size: 180,
              accessorFn: (row: any) =>
                parseScenarioData(
                  row.json_scenario_data,
                  modelsIniFileMap[row.analogue_model_id],
                ).template,
            },
            {
              header: 'Basin slope',
              id: 'basinSlope',
              size: 120,
              accessorFn: (row: any) =>
                parseScenarioData(
                  row.json_scenario_data,
                  modelsIniFileMap[row.analogue_model_id],
                ).basinSlope,
            },
            {
              header: 'Sediment classes',
              id: 'composition',
              size: 150,
              accessorFn: (row: any) =>
                parseScenarioData(
                  row.json_scenario_data,
                  modelsIniFileMap[row.analogue_model_id],
                ).composition,
            },
            {
              header: 'River length',
              id: 'riverLength',
              size: 130,
              accessorFn: (row: any) =>
                parseScenarioData(
                  row.json_scenario_data,
                  modelsIniFileMap[row.analogue_model_id],
                ).riverLength,
            },
            {
              header: 'Simulation time',
              id: 'simStopTime',
              size: 140,
              accessorFn: (row: any) =>
                parseScenarioData(
                  row.json_scenario_data,
                  modelsIniFileMap[row.analogue_model_id],
                ).simStopTime,
            },
            {
              header: 'Channel width',
              id: 'channelWidth',
              size: 130,
              accessorFn: (row: any) =>
                parseScenarioData(
                  row.json_scenario_data,
                  modelsIniFileMap[row.analogue_model_id],
                ).channelWidth,
            },
            {
              header: 'Subsidence at seaward',
              id: 'subsidenceSea',
              size: 180,
              accessorFn: (row: any) =>
                parseScenarioData(
                  row.json_scenario_data,
                  modelsIniFileMap[row.analogue_model_id],
                ).subsidenceSea,
            },
            {
              header: 'Wave direction',
              id: 'waveDirection',
              size: 130,
              accessorFn: (row: any) =>
                parseScenarioData(
                  row.json_scenario_data,
                  modelsIniFileMap[row.analogue_model_id],
                ).waveDirection,
            },
            {
              header: 'Final wave height',
              id: 'waveHeightFin',
              size: 150,
              accessorFn: (row: any) =>
                parseScenarioData(
                  row.json_scenario_data,
                  modelsIniFileMap[row.analogue_model_id],
                ).waveHeightFin,
            },
            {
              header: 'Initial wave height',
              id: 'waveHeightIni',
              size: 150,
              accessorFn: (row: any) =>
                parseScenarioData(
                  row.json_scenario_data,
                  modelsIniFileMap[row.analogue_model_id],
                ).waveHeightIni,
            },
            {
              header: 'Output interval',
              id: 'outputInterval',
              size: 140,
              accessorFn: (row: any) =>
                parseScenarioData(
                  row.json_scenario_data,
                  modelsIniFileMap[row.analogue_model_id],
                ).outputInterval,
            },
            {
              header: 'Subsidence at landward',
              id: 'subsidenceLand',
              size: 180,
              accessorFn: (row: any) =>
                parseScenarioData(
                  row.json_scenario_data,
                  modelsIniFileMap[row.analogue_model_id],
                ).subsidenceLand,
            },
            {
              header: 'Tidal amplitude',
              id: 'tidalAmplitude',
              size: 140,
              accessorFn: (row: any) =>
                parseScenarioData(
                  row.json_scenario_data,
                  modelsIniFileMap[row.analogue_model_id],
                ).tidalAmplitude,
            },
            {
              header: 'Final river discharge',
              id: 'riverDischargeFin',
              size: 170,
              accessorFn: (row: any) =>
                parseScenarioData(
                  row.json_scenario_data,
                  modelsIniFileMap[row.analogue_model_id],
                ).riverDischargeFin,
            },
            {
              header: 'Initial river discharge',
              id: 'riverDischargeIni',
              size: 180,
              accessorFn: (row: any) =>
                parseScenarioData(
                  row.json_scenario_data,
                  modelsIniFileMap[row.analogue_model_id],
                ).riverDischargeIni,
            },
            {
              accessorKey: 'orchestration_status',
              header: 'Status',
              id: 'orchestration_status',
              enableColumnFilter: false,
              size: 120,
            },
            {
              accessorKey: 'created_at',
              header: 'Created',
              id: 'created_at',
              enableColumnFilter: false,
              size: 180,
              cell: ({ row }) =>
                row.original.created_at
                  ? new Date(row.original.created_at).toLocaleString()
                  : '-',
            },
            {
              accessorKey: 'created_by',
              header: 'Created By',
              id: 'created_by',
              enableColumnFilter: false,
              size: 150,
            },
            {
              header: 'Actions',
              id: 'actions',
              enableColumnFilter: false,
              enableResizing: false,
              enableSorting: false,
              minSize: 280,
              cell: ({ row }) => {
                const scenario = row.original as any;
                const isRunning = scenario.orchestration_status === 'Running';
                const isFailed = scenario.orchestration_status === 'Failed';

                return (
                  <Styled.ActionButtons>
                    <ProgressCell
                      orchestrationId={scenario.orchestration_id}
                      isRunning={isRunning}
                      isFailed={isFailed}
                    />
                    <Button
                      variant="outlined"
                      onClick={() =>
                        handleNavigateToSimulation(scenario.scenario_id)
                      }
                    >
                      View
                    </Button>
                  </Styled.ActionButtons>
                );
              },
            },
          ]}
        />
      </Styled.Table>

      <Styled.StyledSideSheet
        open={isSideSheetOpen}
        onClose={() => setIsSideSheetOpen(false)}
        title="Toggle Columns"
      >
        <Styled.SideSheetContent>
          <Styled.SideSheetTitle variant="overline">
            Columns
          </Styled.SideSheetTitle>
          <EdsProvider density="compact">
            <Styled.CheckboxColumn>
              <Checkbox label="Template" checked={true} disabled />
              <Checkbox
                label="Basin slope"
                checked={columnVisibility.basinSlope}
                onChange={() => toggleColumn('basinSlope')}
              />
              <Checkbox
                label="Sediment classes"
                checked={columnVisibility.composition}
                onChange={() => toggleColumn('composition')}
              />
              <Checkbox
                label="River length"
                checked={columnVisibility.riverLength}
                onChange={() => toggleColumn('riverLength')}
              />
              <Checkbox
                label="Simulation time"
                checked={columnVisibility.simStopTime}
                onChange={() => toggleColumn('simStopTime')}
              />
              <Checkbox
                label="Channel width"
                checked={columnVisibility.channelWidth}
                onChange={() => toggleColumn('channelWidth')}
              />
              <Checkbox
                label="Subsidence at seaward"
                checked={columnVisibility.subsidenceSea}
                onChange={() => toggleColumn('subsidenceSea')}
              />
              <Checkbox
                label="Wave direction"
                checked={columnVisibility.waveDirection}
                onChange={() => toggleColumn('waveDirection')}
              />
              <Checkbox
                label="Final wave height"
                checked={columnVisibility.waveHeightFin}
                onChange={() => toggleColumn('waveHeightFin')}
              />
              <Checkbox
                label="Initial wave height"
                checked={columnVisibility.waveHeightIni}
                onChange={() => toggleColumn('waveHeightIni')}
              />
              <Checkbox
                label="Output interval"
                checked={columnVisibility.outputInterval}
                onChange={() => toggleColumn('outputInterval')}
              />
              <Checkbox
                label="Subsidence at landward"
                checked={columnVisibility.subsidenceLand}
                onChange={() => toggleColumn('subsidenceLand')}
              />
              <Checkbox
                label="Tidal amplitude"
                checked={columnVisibility.tidalAmplitude}
                onChange={() => toggleColumn('tidalAmplitude')}
              />
              <Checkbox
                label="Final river discharge"
                checked={columnVisibility.riverDischargeFin}
                onChange={() => toggleColumn('riverDischargeFin')}
              />
              <Checkbox
                label="Initial river discharge"
                checked={columnVisibility.riverDischargeIni}
                onChange={() => toggleColumn('riverDischargeIni')}
              />
              <Checkbox
                label="Status"
                checked={columnVisibility.orchestration_status}
                onChange={() => toggleColumn('orchestration_status')}
              />
              <Checkbox
                label="Created"
                checked={columnVisibility.created_at}
                onChange={() => toggleColumn('created_at')}
              />
            </Styled.CheckboxColumn>
          </EdsProvider>
        </Styled.SideSheetContent>
      </Styled.StyledSideSheet>
    </>
  );
};
